/**
 * Record this camera by hand, apart from the flight.
 *
 * The gateway writes the file; this only turns it on and off and lists what
 * was kept. A recording does not start or end a flight, and a clip taken
 * during a bench arm stays even when the flight log discards the arm.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  downloadRecording,
  listRecordings,
  startRecording,
  stopRecording,
  type Recording,
  type RecordingList,
} from '@/api/video';

function megabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}

function when(epochS: number): string {
  return new Date(epochS * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function Elapsed({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(timer);
  }, []);
  return <>{clock(now - since)}</>;
}

function Clip({ droneId, clip }: { droneId: string; clip: Recording }) {
  const [problem, setProblem] = useState<string | null>(null);
  const files = Array.from({ length: clip.files }, (_, index) => index);
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10px]">
      <span>{when(clip.startedAt)}</span>
      <span className="text-[color:var(--muted-foreground-2)]">
        {clock(clip.durationS)} {megabytes(clip.sizeBytes)}
      </span>
      {clip.stopReason && clip.stopReason !== 'stopped' ? (
        <span className="text-[color:var(--warn)]">{clip.stopReason}</span>
      ) : null}
      {files.length === 0 ? (
        <span className="text-[color:var(--muted-foreground-2)]">no footage written</span>
      ) : null}
      {files.map((index) => (
        <button
          key={index}
          type="button"
          onClick={() =>
            void downloadRecording(droneId, clip.id, index).then((message) => setProblem(message))
          }
          className="label-mono text-[8px] text-[color:var(--muted-foreground-2)] hover:text-[color:var(--foreground)]"
        >
          {files.length > 1 ? `SAVE ${index + 1}` : 'SAVE'}
        </button>
      ))}
      {problem ? <span className="text-[color:var(--danger-foreground)]">{problem}</span> : null}
    </li>
  );
}

export function RecordingPanel({ droneId, canRecord }: { droneId: string; canRecord: boolean }) {
  const [list, setList] = useState<RecordingList | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    void listRecordings(droneId).then((result) => {
      if (result.ok) setList(result.data);
    });
  }, [droneId]);

  useEffect(load, [load]);

  const active = list?.recording ?? null;

  const toggle = async () => {
    setBusy(true);
    setError(null);
    const result = active ? await stopRecording(droneId) : await startRecording(droneId);
    setBusy(false);
    // The server's reason (disk low, already recording, gateway down) is the
    // only part the operator can act on, so it is shown as it came.
    if (!result.ok) setError(result.error.message);
    load();
  };

  if (!list?.enabled) return null;

  const clips = list.recordings.filter((clip) => !clip.active).slice(0, 5);

  return (
    <div className="well flex flex-col gap-1.5 rounded-[11px] px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          RECORDING
        </span>
        {active ? (
          <span className="label-mono text-[8px]" style={{ color: 'var(--danger-foreground)' }}>
            REC <Elapsed since={active.startedAt} />
          </span>
        ) : null}
        <span className="label-mono ml-auto text-[8px] text-[color:var(--muted-foreground-2)]">
          {megabytes(list.disk.usedBytes)} OF {megabytes(list.disk.maxTotalBytes)}
        </span>
        {canRecord ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void toggle()}
            className="well rounded-[99px] px-2 py-0.5 label-mono text-[9px] disabled:opacity-40"
          >
            {active ? 'STOP' : 'RECORD'}
          </button>
        ) : null}
      </div>
      {active ? (
        <p className="text-[9px] leading-snug text-[color:var(--muted-foreground-2)]">
          Recording on the server, apart from any flight. It stops by itself after{' '}
          {Math.round(list.disk.maxDurationS / 60)} minutes or when the disk runs low.
        </p>
      ) : null}
      {error ? (
        <p className="text-[10px] leading-snug text-[color:var(--danger-foreground)]">{error}</p>
      ) : null}
      {clips.length > 0 ? (
        <ul className="flex flex-col gap-0.5" aria-label="Recorded clips">
          {clips.map((clip) => (
            <Clip key={clip.id} droneId={droneId} clip={clip} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
